/**
 * optionsViewPredicate — IJ-230 · Wave 3c · REDLINE-15 modal switch.
 *
 * Pure JS helpers. Zero React import. Trivially unit-testable via
 * `node frontend/src/hooks/__tests__/optionsViewPredicate.test.js`.
 *
 * FSD v1.11 FR-10 · Vague-Query Disambiguation:
 *   The PREDICTIONS view (three completions) and the OPTIONS view
 *   (three-by-two grid + Other field) never render at the same time
 *   (REDLINE-15). Options view fires when intent_confidence is
 *   below 0.55 OR when the LLM returned no completions.
 *
 *   Refusal has its own render path via _enforce_intent_shape and
 *   must not route through Options view — the refusal short-circuit
 *   returns before this predicate is even consulted.
 */

const OPTIONS_VIEW_CONFIDENCE_THRESHOLD = 0.55;

/**
 * True when the frontend should render the Options view (six-slot
 * grid + Other field) instead of the Predictions view.
 *
 * @param {object|null|undefined} data Typeahead response payload.
 * @returns {boolean}
 */
export function shouldShowOptionsView(data) {
  if (!data) return false;
  // Refusal owns its own render path. Never route it through Options.
  if (data.query_class === 'refused') return false;
  const confidence = Number(data.intent_confidence) || 0;
  const hasCompletions = Array.isArray(data.completions) && data.completions.length > 0;
  return confidence < OPTIONS_VIEW_CONFIDENCE_THRESHOLD || !hasCompletions;
}

/**
 * Pick up to three example hints for the Other field's italic
 * '[for example: ...]' phrase, drawn from the current chips. Returns
 * an empty array on low-signal input so the caller can render the
 * '[type here to keep searching]' placeholder instead.
 *
 * @param {string[]|null|undefined} chips
 * @returns {string[]}
 */
export function pickExampleHints(chips) {
  if (!Array.isArray(chips) || chips.length === 0) return [];
  return chips.slice(0, 3).map(String);
}

export const _INTERNAL = { OPTIONS_VIEW_CONFIDENCE_THRESHOLD };


/**
 * IJ-231 · Return the render mode the OptionsPrimitive should use.
 * Called AFTER `shouldShowOptionsView(data)` has already returned
 * true (the caller has decided to route to Options view). Returns:
 *   · 'rich'       — data.option_set is a non-empty array. Render
 *                    each slot with label + one_line_hint per FR-10.
 *   · 'plain'      — data.chips is non-empty. Render plain chips.
 *   · 'other-only' — both option_set and chips are empty. Skip the
 *                    grid, render only the Other field.
 *
 * @param {object|null|undefined} data
 * @returns {'rich'|'plain'|'other-only'}
 */
export function renderModeForOptions(data) {
  if (!data) return 'other-only';
  const optionSet = Array.isArray(data.option_set) ? data.option_set : [];
  if (optionSet.length > 0) return 'rich';
  const chips = Array.isArray(data.chips) ? data.chips : [];
  if (chips.length > 0) return 'plain';
  return 'other-only';
}
